/**
 * Crawler/bot detection via user-agent sniffing.
 *
 * Deliberately an explicit allowlist of known crawler tokens rather than a
 * generic `/bot|spider/` pattern: generic patterns false-positive on real
 * devices (e.g. Cubot phones embed "CUBOT" in Android UAs) and on the
 * headless browsers our own QA Wolf / synthetic tests drive through the
 * assessment funnel. A missed crawler only costs a little noise; flagging a
 * real visitor as a bot would silently break their assessment session.
 */
const KNOWN_CRAWLER_UA = new RegExp(
  [
    // Search engines. Deliberately NOT listed: AdsBot-Google and
    // Mediapartners-Google — ads crawlers evaluate ad-destination quality,
    // ignore `userAgent: *` robots rules, and must experience the real funnel
    // (tenants run paid ads into these pages), not a session-less spinner.
    "googlebot",
    "googleother",
    "google-inspectiontool",
    "storebot-google",
    "bingbot",
    "bingpreview",
    "slurp", // Yahoo
    "duckduckbot",
    "baiduspider",
    "yandex(?:bot|images|mobilebot)",
    "applebot",
    "petalbot",
    "bytespider",
    // Link-preview fetchers (NOT the in-app browsers: FBAN/FBAV, Instagram,
    // and "Pinterest for Android/iOS" UAs are real users and must not match)
    "facebookexternalhit",
    "meta-externalagent",
    "twitterbot",
    "linkedinbot",
    "pinterestbot",
    "telegrambot",
    // Anchored: the preview fetcher's UA IS the token ("WhatsApp/2.x.y.z"),
    // while real WhatsApp in-app browser UAs are full Mozilla strings that can
    // END with "WhatsApp/…" — those are real users and must not match.
    "^whatsapp/",
    "slackbot",
    "discordbot",
    // AI crawlers
    "gptbot",
    "oai-searchbot",
    "chatgpt-user",
    "claudebot",
    "claude-web",
    "anthropic-ai",
    "perplexitybot",
    "ccbot",
    "amazonbot",
    // SEO tooling
    "semrushbot",
    "ahrefsbot",
    "mj12bot",
    "dotbot",
    "screaming frog",
    "ia_archiver", // Wayback Machine
  ].join("|"),
  "i",
);

export function isBotUserAgent(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : undefined);
  if (!ua) {
    return false;
  }
  return KNOWN_CRAWLER_UA.test(ua);
}

// Chromium headless replaces the "Chrome/" UA token with "HeadlessChrome/";
// headless Edge builds report "HeadlessEdg/" alongside or instead of it.
const HEADLESS_WEB_CLIENT_UA = /headless(?:chrome|edg)/i;

/**
 * Headless Chromium automation (scrapers, synthetic runners) that is not
 * covered by {@link isBotUserAgent}. Used only to soften empty-token define
 * failures — QA Wolf still calls define and must receive a real token.
 */
export function isHeadlessWebClient(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : undefined);
  if (!ua) {
    return false;
  }
  return HEADLESS_WEB_CLIENT_UA.test(ua);
}

/**
 * Crawlers and headless automation. Note that headless clients CAN complete
 * interactive flows (QA Wolf synthetics drive the real assessment funnel
 * headlessly), so this is a noise-softening signal for expected failures —
 * never use it to gate the define call or other session behavior.
 */
export function isNonInteractiveWebClient(userAgent?: string): boolean {
  return isBotUserAgent(userAgent) || isHeadlessWebClient(userAgent);
}
